import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Inbox, Layers, FileText, LogOut, Plus, ShieldCheck, Settings, MailX, Menu, X, BarChart3, Timer } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { useMe } from '@/features/me/useMe';
import type { Role } from '@/features/me/meApi';
import { cn } from '@/lib/utils';

/**
 * Authenticated app shell — sidebar + top bar + content slot.
 *
 * Responsive layout (HANDOFF §1.14):
 * - md and up: fixed left sidebar.
 * - below md: a hamburger button in the header opens a slide-over drawer that
 *   reuses the same nav. The drawer closes when a nav item is picked or the
 *   backdrop is tapped, and Escape closes it too.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const account = accounts[0];
  const me = useMe();
  const isIT = me.data?.role === 'ITAgent' || me.data?.role === 'ITAdmin';
  const isAdmin = me.data?.role === 'ITAdmin';

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close the drawer whenever the route changes (i.e. user picked a nav item).
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Esc closes the drawer + body scroll lock while it's open (mobile UX).
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  return (
    // h-screen + overflow-hidden makes the layout exactly one viewport tall, so
    // the only thing that scrolls is the content <section>. That keeps the
    // header sticky and the sidebar's theme/sign-out footer always visible no
    // matter how long the ticket thread is. (h-dvh would track iOS Safari's
    // dynamic viewport but isn't critical — the mobile drawer is fixed-position.)
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="w-60 hidden md:flex flex-col border-r bg-card">
        <ShellBrand />
        <NavList isIT={isIT} isAdmin={isAdmin} />
        <ShellFooter onSignOut={() => instance.logoutRedirect()} />
      </aside>

      {/* Mobile drawer (slide-over) */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isIT={isIT}
        isAdmin={isAdmin}
        onSignOut={() => instance.logoutRedirect()}
      />

      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b flex items-center px-3 sm:px-5 gap-3 bg-card/60 backdrop-blur sticky top-0 z-10">
          {/* Mobile-only hamburger */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="mobile-nav-drawer"
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-muted -ml-1"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Compact title on mobile (no sidebar to show the brand) */}
          <div className="md:hidden text-sm font-semibold truncate">AES I.T. Helpdesk</div>

          {/* Desktop breadcrumb */}
          <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
            <span>Workspace</span>
            <span className="opacity-40">/</span>
            <span className="text-foreground font-medium">
              {location.pathname.replace(/^\//, '') || 'home'}
            </span>
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-3 min-w-0">
            {/* IT staff only — end users don't need a chip telling them they're an end user. */}
            {isIT && me.data && <RoleBadge role={me.data.role} />}
            {/* Hide the long email on phones (keep on tablets+) */}
            <span className="hidden sm:inline text-sm text-muted-foreground truncate max-w-[14rem]">
              {account?.name ?? account?.username}
            </span>
          </div>
        </header>
        <section className="flex-1 overflow-auto">{children}</section>
      </main>
    </div>
  );
}

/**
 * Brand block, shared by the sidebar and the mobile drawer. The logo already
 * carries the "AES" wordmark, so we drop the redundant "AES I.T. Helpdesk"
 * label here and keep only the small "I.T. Helpdesk · v0.1" qualifier.
 */
function ShellBrand() {
  return (
    <div className="px-4 py-4 border-b">
      <img src="/aes-logo.svg" alt="AES" className="h-10 w-auto" />
      <div className="mt-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        I.T. Helpdesk · v0.1
      </div>
    </div>
  );
}

/**
 * The actual link list. One source of truth for sidebar + drawer.
 *
 * `flex-1 overflow-y-auto` makes the nav itself the scrolling surface inside
 * the shell column, so the brand (top) and ShellFooter (bottom: theme toggle +
 * sign out) stay pinned even when the nav list overflows.
 */
function NavList({ isIT, isAdmin }: { isIT: boolean; isAdmin: boolean }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 text-sm">
      <ShellNavLink to="/new-ticket" icon={<Plus className="w-4 h-4" />}>New Ticket</ShellNavLink>
      <ShellNavLink to="/tickets" icon={<Inbox className="w-4 h-4" />}>My Tickets</ShellNavLink>
      {isIT && <ShellNavLink to="/queue" icon={<Layers className="w-4 h-4" />}>Queue</ShellNavLink>}
      <ShellNavLink to="/tickets" icon={<FileText className="w-4 h-4" />}>Knowledge Base</ShellNavLink>

      {isAdmin && (
        <div className="pt-4">
          <div className="px-3 pb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Settings className="w-3 h-3" /> Settings
          </div>
          <ShellNavLink to="/settings/reports" icon={<BarChart3 className="w-4 h-4" />}>Reports</ShellNavLink>
          <ShellNavLink to="/settings/sla-targets" icon={<Timer className="w-4 h-4" />}>SLA targets</ShellNavLink>
          <ShellNavLink to="/settings/applications" icon={<Layers className="w-4 h-4" />}>Applications</ShellNavLink>
          <ShellNavLink to="/settings/ignored-senders" icon={<MailX className="w-4 h-4" />}>Ignored senders</ShellNavLink>
        </div>
      )}
    </nav>
  );
}

/** Theme + sign-out, shared by sidebar and drawer. */
function ShellFooter({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div className="mt-auto p-3 border-t space-y-2">
      <ThemeToggle />
      <button
        type="button"
        onClick={onSignOut}
        className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground border rounded-md py-2"
      >
        <LogOut className="w-3.5 h-3.5" /> Sign out
      </button>
    </div>
  );
}

/**
 * Slide-over mobile nav. Always rendered; visibility + slide are controlled by
 * the `open` prop (so the slide-out animation runs both ways). md+ never sees it
 * because the trigger (the hamburger) is `md:hidden`.
 */
function MobileDrawer({
  open,
  onClose,
  isIT,
  isAdmin,
  onSignOut
}: {
  open: boolean;
  onClose: () => void;
  isIT: boolean;
  isAdmin: boolean;
  onSignOut: () => void;
}) {
  return (
    <div
      id="mobile-nav-drawer"
      className={cn('md:hidden fixed inset-0 z-30', open ? 'pointer-events-auto' : 'pointer-events-none')}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={cn('absolute inset-0 bg-black/50 transition-opacity', open ? 'opacity-100' : 'opacity-0')}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Navigation"
        className={cn(
          'absolute inset-y-0 left-0 w-72 max-w-[85vw] flex flex-col border-r bg-card shadow-xl',
          'transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between border-b pr-2">
          <ShellBrand />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <NavList isIT={isIT} isAdmin={isAdmin} />
        <ShellFooter onSignOut={onSignOut} />
      </aside>
    </div>
  );
}

/**
 * Header badge showing the signed-in user's role. IT roles get a tinted,
 * shield-marked badge so an agent/admin can tell at a glance they're operating
 * with elevated access; end users get a plain, muted label.
 */
function RoleBadge({ role }: { role: Role }) {
  const label = role === 'ITAdmin' ? 'IT Admin' : role === 'ITAgent' ? 'IT Agent' : 'End User';
  const isIT = role === 'ITAdmin' || role === 'ITAgent';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        isIT
          ? 'bg-primary/15 text-primary'
          : 'bg-muted text-muted-foreground'
      )}
      title={`Signed in as ${label}`}
    >
      {isIT && <ShieldCheck className="w-3.5 h-3.5" />}
      {label}
    </span>
  );
}

/**
 * Sidebar/drawer link. Primary-tinted "active" state via NavLink. Larger
 * vertical padding (py-2.5) so tap targets meet ~44px on mobile.
 */
function ShellNavLink({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 px-3 py-2.5 rounded-md hover:bg-muted text-foreground/80',
          isActive && 'bg-primary/10 text-primary'
        )
      }
    >
      {icon}
      <span>{children}</span>
    </NavLink>
  );
}
